import { KpiTileViewModel } from 'src/app/dashboards/common/items/kpi-tiles/kpi-tiles.component';
import {
    getResourceFlowMetric,
    getResourceFlowMetricSettings,
    withFlowUserType,
} from 'src/app/dashboards/mopa-resource-flow/resource-flow.models';
import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    IQueryContextDatasourceColumn,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardResourcesSqlMap } from '../../mopa-resources-detailed/sql-maps/biviz-dashboard-resources-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface ResourceFlowKpiDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/**
 * Produces Resource Flow's headline tiles from one global daily series for the applied filters.
 * It deliberately ignores the chart's Split, Group and zoom state: chart interaction must never
 * trigger another BigQuery query or make the headline values depend on a client-side window.
 *
 * Every value mode states its own complete query so the emitted SQL reads as SQL.
 */
export class ResourceFlowKpiDataLoaderFactory extends Factory<
    DataLoader,
    ResourceFlowKpiDataLoaderFactoryOptions
> {
    override FACTORY_CLASS = 'ResourceFlowKpiDataLoaderFactory';

    getInstance(options: ResourceFlowKpiDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const resourcesSqlMap = BiVizDashboardResourcesSqlMap.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    resourcesSqlMap.columns
                );
                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                // KPI keeps filters, not chart Split or Group.
                const resourcesSQL =
                    BiVizDashboardResourcesSqlMap.sqlStatementsCompiler.compileSQLStatements(
                        withoutSplitOrBucket(dsColumns.present),
                        queryContext.settings,
                        options
                    );

                const currency =
                    queryContext.settings[
                        QueryContextSettingID.RESOURCE_FLOW_CURRENCY
                    ] ?? 'gems';
                const { valueMode, population, percentiles } =
                    getResourceFlowMetricSettings(queryContext);
                const metric = getResourceFlowMetric(queryContext);

                if (valueMode === 'percentile' && !percentiles.length) {
                    throw new Error(
                        'Resource Flow KPI: percentile mode needs at least one selected percentile'
                    );
                }

                // Average and Percentile both offer the DAU population.
                const usesDauPopulation =
                    (valueMode === 'average' || valueMode === 'percentile') &&
                    population === 'dau';

                const usersdashActivity = BiVizDashboardUsersActivity;
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );
                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        withoutSplitOrBucket(
                            usersdashActivityDsColumns.present
                        ),
                        queryContext.settings,
                        options
                    );

                const resourcesTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    resourcesSqlMap.dataset,
                    resourcesSqlMap.tableId
                );
                const usersdashActivityTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    usersdashActivity.sqlMap.dataset,
                    usersdashActivity.sqlMap.tableId
                );

                // User Type filters flow and active users by the same daily flag.
                const userType =
                    queryContext.settings[
                        QueryContextSettingID.FLOW_USER_TYPE
                    ] ?? 'all';
                const resourcesWhere = withFlowUserType(
                    resourcesSQL.where,
                    userType,
                    'resources'
                );
                const usersActivityWhere = withFlowUserType(
                    usersdashActivitySQL.where,
                    userType,
                    'activity'
                );
                // DAU metrics report the full active population.
                const populationQuery = `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow KPI affected players - currency: ${currency}, population: ${population}

WITH flow_players AS
(
  SELECT
      ds.player_id
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
  GROUP BY ds.player_id
  HAVING COUNTIF(
      ds.${currency}_inflow > 0 OR ds.${currency}_outflow > 0
  ) > 0
)
, active_users AS
(
  SELECT
      DISTINCT ds.player_id
  FROM \`${usersdashActivityTablePath}\` ds
  ${usersdashActivitySQL.join}
  ${usersActivityWhere}
)
SELECT
    SAFE_DIVIDE(
        COUNT(${
            usesDauPopulation
                ? 'active_users.player_id'
                : 'flow_players.player_id'
        })
      , COUNT(active_users.player_id)
    ) AS population_ratio
  , COUNT(${
      usesDauPopulation ? 'active_users.player_id' : 'flow_players.player_id'
  }) AS player_count
  , COUNT(active_users.player_id) AS total_player_count
FROM active_users
LEFT JOIN flow_players USING (player_id)
`;
                const populationResponsePromise = DataLoader.runQuery(
                    populationQuery,
                    options.queryCacheService
                );

                const buildQuery = (): string => {
                    if (valueMode === 'total') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow KPI - currency: ${currency}, mode: total

SELECT
    FORMAT_DATE('%Y-%m-%d', DATE(${resourcesSQL.dateTrunc()})) AS date
  , ROUND(SUM(ds.${currency}_inflow),  2) AS inflow
  , ROUND(SUM(ds.${currency}_outflow), 2) AS outflow
FROM \`${resourcesTablePath}\` ds
${resourcesSQL.join}
${resourcesWhere}
GROUP BY date
ORDER BY date
`;
                    }

                    if (valueMode === 'flow records') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow KPI - currency: ${currency}, mode: flow records

SELECT
    FORMAT_DATE('%Y-%m-%d', DATE(${resourcesSQL.dateTrunc()})) AS date
  , COUNTIF(ds.${currency}_inflow > 0)  AS inflow
  , COUNTIF(ds.${currency}_outflow > 0) AS outflow
FROM \`${resourcesTablePath}\` ds
${resourcesSQL.join}
${resourcesWhere}
GROUP BY date
ORDER BY date
`;
                    }

                    if (valueMode === 'players') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow KPI - currency: ${currency}, mode: players

SELECT
    FORMAT_DATE('%Y-%m-%d', DATE(${resourcesSQL.dateTrunc()})) AS date
  , COUNT(DISTINCT IF(ds.${currency}_inflow > 0, ds.player_id, NULL))  AS inflow
  , COUNT(DISTINCT IF(ds.${currency}_outflow > 0, ds.player_id, NULL)) AS outflow
FROM \`${resourcesTablePath}\` ds
${resourcesSQL.join}
${resourcesWhere}
GROUP BY date
ORDER BY date
`;
                    }

                    if (valueMode === 'average' && population === 'deu') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow KPI - currency: ${currency}, mode: average per deu

SELECT
    FORMAT_DATE('%Y-%m-%d', DATE(${resourcesSQL.dateTrunc()})) AS date
  , ROUND(SAFE_DIVIDE(
        SUM(ds.${currency}_inflow),
        COUNT(DISTINCT IF(ds.${currency}_inflow > 0, ds.player_id, NULL))
    ), 2) AS inflow
  , ROUND(SAFE_DIVIDE(
        SUM(ds.${currency}_outflow),
        COUNT(DISTINCT IF(ds.${currency}_outflow > 0, ds.player_id, NULL))
    ), 2) AS outflow
FROM \`${resourcesTablePath}\` ds
${resourcesSQL.join}
${resourcesWhere}
GROUP BY date
ORDER BY date
`;
                    }

                    if (valueMode === 'average' && population === 'dau') {
                        // The active population drives the series: a date bucket without any resource
                        // flow still reports its DAU denominator, hence the LEFT JOIN from active_users.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow KPI - currency: ${currency}, mode: average per dau

WITH resources AS
(
  SELECT
      DATE(${resourcesSQL.dateTrunc()}) AS date
    , SUM(ds.${currency}_inflow)  AS inflow
    , SUM(ds.${currency}_outflow) AS outflow
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
  GROUP BY date
)
, active_users AS
(
  SELECT
      DATE(ds.date) AS date
    , COUNT(DISTINCT ds.player_id) AS dau
  FROM \`${usersdashActivityTablePath}\` ds
  ${usersdashActivitySQL.join}
  ${usersActivityWhere}
  GROUP BY date
)
SELECT
    FORMAT_DATE('%Y-%m-%d', active_users.date) AS date
  , ROUND(SAFE_DIVIDE(COALESCE(resources.inflow, 0), active_users.dau), 2)  AS inflow
  , ROUND(SAFE_DIVIDE(COALESCE(resources.outflow, 0), active_users.dau), 2) AS outflow
FROM active_users
LEFT JOIN resources USING (date)
ORDER BY date
`;
                    }

                    if (valueMode === 'percentile' && population === 'deu') {
                        // The percentile answers "the typical player over the applied range", so it is
                        // taken once over the pooled player/date-bucket values instead of averaging
                        // daily percentiles. `deu` counts only players with a positive flow on that
                        // side (APPROX_QUANTILES skips NULL).
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow KPI - currency: ${currency}, mode: P${percentiles[0]} per deu

WITH perPlayerDate AS
(
  SELECT
      DATE(${resourcesSQL.dateTrunc()}) AS date
    , ds.player_id
    , SUM(ds.${currency}_inflow)  AS inflow
    , SUM(ds.${currency}_outflow) AS outflow
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
  GROUP BY date, player_id
)
SELECT
    APPROX_QUANTILES(IF(inflow > 0, inflow, NULL), 100)[OFFSET(${
        percentiles[0]
    })]   AS inflow
  , APPROX_QUANTILES(IF(outflow > 0, outflow, NULL), 100)[OFFSET(${
      percentiles[0]
  })] AS outflow
FROM perPlayerDate
`;
                    }

                    if (valueMode === 'percentile' && population === 'dau') {
                        // `dau` percentiles every active player and reads a missing flow as zero, so the
                        // per-player values are first joined onto the whole active population.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow KPI - currency: ${currency}, mode: P${percentiles[0]} per dau

WITH perPlayerDate AS
(
  SELECT
      DATE(${resourcesSQL.dateTrunc()}) AS date
    , ds.player_id
    , SUM(ds.${currency}_inflow)  AS inflow
    , SUM(ds.${currency}_outflow) AS outflow
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
  GROUP BY date, player_id
)
, dauPlayers AS
(
  SELECT
      DATE(${resourcesSQL.dateTrunc()}) AS date
    , ds.player_id
  FROM \`${usersdashActivityTablePath}\` ds
  ${usersdashActivitySQL.join}
  ${usersActivityWhere}
  GROUP BY date, player_id
)
, population AS
(
  SELECT
      COALESCE(perPlayerDate.inflow, 0)  AS inflow
    , COALESCE(perPlayerDate.outflow, 0) AS outflow
  FROM dauPlayers
  LEFT JOIN perPlayerDate USING (date, player_id)
)
SELECT
    APPROX_QUANTILES(inflow, 100)[OFFSET(${percentiles[0]})]  AS inflow
  , APPROX_QUANTILES(outflow, 100)[OFFSET(${percentiles[0]})] AS outflow
FROM population
`;
                    }

                    // A new value mode or population must state its own KPI query instead of silently
                    // inheriting another one.
                    throw new Error(
                        `Resource Flow KPI: no query defined for value mode '${valueMode}' and population '${population}'`
                    );
                };

                const query = buildQuery();

                const [response, populationResponse] = await Promise.all([
                    DataLoader.runQuery(query, options.queryCacheService),
                    populationResponsePromise,
                ]);
                cancellationToken.throwIfCancellationRequested();

                const rows = [...response.result];
                let inflow: number;
                let outflow: number;
                if (valueMode === 'percentile') {
                    // Already one range-wide value per side; there is no daily series to reduce.
                    const row = rows[0];
                    if (!row) {
                        throw new Error(
                            'Resource Flow KPI: percentile query returned no row'
                        );
                    }
                    inflow = +(row.inflow ?? 0) || 0;
                    outflow = +(row.outflow ?? 0) || 0;
                } else {
                    const dateAggregation =
                        queryContext.settings[
                            QueryContextSettingID.DATE_AGGREGATION
                        ] ?? 'day';
                    XYChartAxis.fillMissingTimeGapRows(
                        rows,
                        queryContext,
                        'date',
                        dateAggregation,
                        { dimensionKeys: [] }
                    );
                    inflow = rangeValue(
                        rows.map((row) => +(row.inflow ?? 0) || 0),
                        metric.rangeAggregation
                    );
                    outflow = rangeValue(
                        rows.map((row) => +(row.outflow ?? 0) || 0),
                        metric.rangeAggregation
                    );
                }
                const unit =
                    metric.unit === 'currency' ? currency : metric.unit;
                const net = inflow - outflow;
                const percentileSuffix =
                    valueMode === 'percentile' ? ` (P${percentiles[0]})` : '';
                const populationRatio =
                    +(populationResponse.result[0]?.population_ratio ?? 0) || 0;
                const playerCount =
                    +(populationResponse.result[0]?.player_count ?? 0) || 0;
                const totalPlayerCount =
                    +(populationResponse.result[0]?.total_player_count ?? 0) ||
                    0;
                const data: KpiTileViewModel[] = [
                    {
                        label: `Inflow${percentileSuffix}`,
                        value: inflow,
                        unit,
                        tone: 'positive',
                        indicator: 'up',
                    },
                    {
                        label: `Outflow${percentileSuffix}`,
                        value: outflow,
                        unit,
                        tone: 'negative',
                        indicator: 'down',
                    },
                    {
                        label: `Net balance${percentileSuffix}`,
                        value: net,
                        unit,
                        tone: net >= 0 ? 'positive' : 'negative',
                        indicator: net >= 0 ? 'up' : 'down',
                    },
                    {
                        label: 'Affected players',
                        value: populationRatio,
                        unit: `${playerCount.toLocaleString(
                            'en-US'
                        )} of ${totalPlayerCount.toLocaleString(
                            'en-US'
                        )} players`,
                        tone: 'neutral',
                        indicator: 'none',
                        valueFormatter: (value) =>
                            `${(value * 100).toFixed(1)}%`,
                    },
                ];
                this.latestData = data;
                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: `${query}\n\n${populationQuery}`,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}

/** KPI queries keep filters but intentionally never inherit chart Split or Group dimensions. */
function withoutSplitOrBucket(
    columns: IQueryContextDatasourceColumn[]
): IQueryContextDatasourceColumn[] {
    return columns.map((column) => ({
        ...column,
        state: {
            ...column.state,
            isSplitApplied: false,
            isBucketApplied: false,
        },
    }));
}

function rangeValue(values: number[], aggregation: 'sum' | 'mean'): number {
    if (!values.length) {
        return 0;
    }
    const sum = values.reduce((total, value) => total + value, 0);
    return aggregation === 'sum' ? sum : sum / values.length;
}
